import { useMemo } from 'react';
import Cookie from 'js-cookie';
import { useEnvironment } from '../context/Environment/EnvironmentContext';
import Environment from '../constants/Environment';

interface Configuration {
  basePath: string;
  accessToken?: string;
}

export class V30Service {
  private config: Configuration;

  constructor(config: Configuration) {
    this.config = config;
  }

  async fetchResources(path: string): Promise<Response> {
    const options = this.getRequestOptions();
    try {
      return fetch(this.config.basePath + path, options);
    } catch (error) {
      throw new Error('Could not fetch resources');
    }
  }

  getRequestOptions(): object {
    if (this.config.accessToken) {
      return {
        headers: {
          Authorization: `Bearer ${this.config.accessToken}`,
          'EVE-ORIGIN': 'EVERIFY_UI',
        },
      };
    }
    return {};
  }

  async getCasesAlerts(): Promise<Response> {
    return this.fetchResources(`/cases/alert_counts`);
  }
}

const useConfiguration = (): Configuration => {
  const { ENVIRONMENT_NAME } = useEnvironment();
  const useNewToken = [
    Environment.LOCAL,
    Environment.DEVTEST,
    Environment.SYSTEST,
    Environment.EXTERNALTEST,
    Environment.PREVIEW,
    Environment.PROD,
  ].includes(ENVIRONMENT_NAME);
  const tokenName = useNewToken ? '_auth_token' : 'cases-ui-access-token';
  const BASE_URL = '/api/v31';
  const eveCasesUIAccessToken = Cookie.get(tokenName);

  const configuration: Configuration = useMemo(() => {
    return { basePath: BASE_URL, accessToken: eveCasesUIAccessToken as string };
  }, [BASE_URL, eveCasesUIAccessToken]);

  return configuration;
};

const useV30Service = (): V30Service => {
  const configuration = useConfiguration();
  const service = useMemo(() => new V30Service(configuration), [configuration]);

  return service;
};

export default useV30Service;
